'use client';
import React from 'react';
import Link from 'next/link';
import { Check } from 'lucide-react';
import { MembershipOffer } from '@/lib/membership/useMembershipOffers';

interface OfferCardProps {
  offer: MembershipOffer;
  featured?: boolean;
  isCurrent?: boolean;
  hideJoin?: boolean;
}

function pounds(pence: number | null): string {
  if (pence === null) return '';
  const p = pence / 100;
  return Number.isInteger(p) ? `£${p}` : `£${p.toFixed(2)}`;
}

export default function OfferCard({ offer, featured = false, isCurrent = false, hideJoin = false }: OfferCardProps) {
  const f = featured;
  const left = offer.foundingPlacesLeft ?? 0;
  const showFounding =
    offer.foundingPlaces !== null && offer.foundingPlaces > 0 && left > 0 && offer.foundingPricePence !== null;
  const billingLabel = offer.billing ? ` ${offer.billing}` : '';
  const muted = f ? 'text-white/70' : 'text-[#2b1a22]/60';

  return (
    <div
      className={`relative flex h-full flex-col rounded-2xl border p-6 sm:p-8 ${
        f ? 'border-[#C6A667] bg-[#581C32] text-white shadow-xl' : 'border-[#C6A667]/50 bg-white text-[#2b1a22]'
      } ${isCurrent ? 'ring-2 ring-[#C6A667] ring-offset-2 ring-offset-[#F8F3E9]' : ''}`}
    >
      {isCurrent && (
        <span className="absolute -top-3 right-5 rounded-full bg-[#C6A667] px-3 py-1 text-[11px] font-bold uppercase tracking-[0.06em] text-[#581C32] shadow">
          Your current plan
        </span>
      )}
      {offer.tagline && (
        <p className={`text-[11px] font-bold uppercase tracking-[0.12em] ${f ? 'text-[#C6A667]' : 'text-[#8a6d2f]'}`}>
          {offer.tagline}
        </p>
      )}
      <h3 className="mt-1.5 text-xl font-bold tracking-tight">{offer.name}</h3>

      <p className="mt-5 flex flex-wrap items-baseline gap-x-1.5">
        <span className={`text-4xl font-extrabold tracking-tight ${f ? 'text-[#C6A667]' : 'text-[#581C32]'}`}>
          {pounds(offer.currentPricePence)}
        </span>
        <span className={`text-sm ${muted}`}>{offer.billing}</span>
      </p>

      {showFounding && (
        <p className={`mt-2 text-xs leading-relaxed ${f ? 'text-white/80' : 'text-[#2b1a22]/70'}`}>
          Founding rate: {pounds(offer.foundingPricePence)}
          {offer.billing ? `/${offer.billing.replace(/^per\s+/i, '')}` : ''} for the first {offer.foundingPlaces} members ({left} left).
          {offer.standardPricePence !== null && (
            <>
              {' '}Then {pounds(offer.standardPricePence)}
              {offer.billing ? `/${offer.billing.replace(/^per\s+/i, '')}` : ''}.
            </>
          )}
        </p>
      )}

      {offer.audience && (
        <p className={`mt-4 text-sm leading-relaxed ${f ? 'text-white/85' : 'text-[#2b1a22]/80'}`}>{offer.audience}</p>
      )}

      {offer.features.length > 0 && (
        <ul className="mt-6 space-y-3">
          {offer.features.map((item, i) => (
            <li key={`${offer.route}-f-${i}`} className="flex items-start gap-2.5 text-sm">
              <Check size={16} className={`mt-0.5 flex-shrink-0 ${f ? 'text-[#C6A667]' : 'text-[#581C32]'}`} />
              <span className={f ? 'text-white/90' : 'text-[#2b1a22]'}>{item}</span>
            </li>
          ))}
        </ul>
      )}

      {offer.notIncluded && <p className={`mt-5 text-xs ${f ? 'text-white/60' : 'text-gray-500'}`}>{offer.notIncluded}</p>}

      <div className="mt-auto pt-6">
        {isCurrent ? (
          <span
            className={`inline-flex w-full items-center justify-center rounded-lg border px-4 py-3 text-sm font-semibold ${
              f ? 'border-[#C6A667] text-[#C6A667]' : 'border-[#581C32] text-[#581C32]'
            }`}
          >
            Your current plan
          </span>
        ) : (
          !hideJoin &&
          offer.paymentUrl && (
            <>
              <a
                href={offer.paymentUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={`inline-flex w-full items-center justify-center rounded-lg px-4 py-3 text-sm font-semibold transition-all duration-150 active:scale-95 ${
                  f ? 'bg-[#C6A667] text-[#581C32] hover:brightness-95' : 'bg-[#581C32] text-white hover:bg-[#581C32]/90'
                }`}
              >
                Join
              </a>
              <p className={`mt-2 text-center text-xs ${muted}`}>Please pay with the same email address you log in with</p>
            </>
          )
        )}
        <div className="mt-3 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-xs">
          <Link
            href="/terms-and-conditions"
            className={`font-semibold underline underline-offset-2 ${f ? 'text-[#C6A667]' : 'text-[#581C32]'}`}
          >
            View terms
          </Link>
          <Link
            href="/cancellation-policy"
            className={`font-semibold underline underline-offset-2 ${f ? 'text-[#C6A667]' : 'text-[#581C32]'}`}
          >
            Cancellation policy
          </Link>
        </div>
      </div>
    </div>
  );
}
